import type { ApiProblem } from '@rental/contracts/client'
import type { InspectionStage, VehicleZone } from '@rental/domain'

/**
 * S4 (OPS-5): what the iPad keeps of an inspection until the server has it.
 * Every photo and every damage is written here first, online or not, and
 * sent from here (drainer.ts): one path for good signal and none.
 *
 * - queued: waiting to be sent, never tried or trying again later ("In attesa");
 * - sending: on its way ("Invio…");
 * - sent: the server has it; kept until the inspection it belongs to lists it,
 *   so it never vanishes from the screen in between ("Inviata");
 * - refused: the server will not take it as it is ("Non inviata"), with why.
 */
export type CaptureState = 'queued' | 'sending' | 'sent' | 'refused'

/** Who took it: it is sent only while they are unlocked (PRD: every action is attributed). */
export interface CapturedBy {
  userId: string
  name: string
}

interface CaptureBase {
  /** Made on the iPad; also the client id the server records it under. */
  id: string
  bookingCode: string
  stage: InspectionStage
  vehicleId: string
  plate: string
  zone: VehicleZone
  capturedBy: CapturedBy
  /** The enrolment it was captured under; for the list, never a reason to send or not. */
  deviceId: string
  /** The iPad's clock, for the order and the age shown; never sent as the time it happened. */
  capturedAt: number
  state: CaptureState
  /** Failed sends since the last success or retry; drives the wait before the next. */
  attempts: number
  nextAttemptAt: number
  /** The last answer that kept it here: a dropped connection, a refusal. */
  problem: ApiProblem | null
  /** The server's id for it once recorded: the photo's or the damage's. */
  serverId: string | null
  sentAt: number | null
}

export interface PhotoItem extends CaptureBase {
  kind: 'photo'
  /**
   * The JPEG as re-encoded on the iPad (photos/jpeg.ts): no EXIF, no position.
   * Kept as bytes, not a Blob: WebKit's IndexedDB cannot serialise a Blob, so
   * a Blob here would abort the write and the photo would never be saved. The
   * screen and the drainer rebuild a Blob from these bytes when they need one.
   */
  bytes: ArrayBuffer
  sizeBytes: number
  /** The damage it shows, once the server knows it. */
  damageId: string | null
  /** The damage it shows while that damage is still on this iPad: its item id. */
  damageItemId: string | null
}

export interface DamageItem extends CaptureBase {
  kind: 'damage'
  description: string
}

export type CaptureItem = PhotoItem | DamageItem

/** The booking, stage and vehicle an inspection item belongs to. */
export interface InspectionKey {
  bookingCode: string
  stage: InspectionStage
  vehicleId: string
}

export function sameInspection(item: CaptureItem, key: InspectionKey): boolean {
  return (
    item.bookingCode === key.bookingCode.toUpperCase() &&
    item.stage === key.stage &&
    item.vehicleId === key.vehicleId
  )
}

/** A device-made id (OPS-5); crypto.randomUUID needs a secure context, so a fallback. */
export function newClientId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80
  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}
